// Multi-manager buyer teams (WS4) — the registry, one team's detail, and the
// roster. Mirrors `BuyerTeamsController` (api/buyer-teams) field for field:
// reads gate on `Buyers.Read` (a specialist may see which team they are on),
// every mutation on `BuyerTeams.Manage` (org structure, not dispatching).
//
// 🔴 Membership is ONE action either way. `addMember` on a person who is
// already on another team MOVES them — a person is on exactly one team (D1) —
// so any UI that calls it owes the reader that sentence before it fires.

import { api } from "./client";

/** An archived team keeps its members, its leads and its history; it drops out
 *  of the picker and refuses new members. */
export type BuyerTeamStatus = "Open" | "Archived";

/** The two seats on a roster. Mirrors `BuyerTeamRoles` — the server rejects
 *  anything else with a 400. */
export type BuyerTeamRole = "Manager" | "Specialist";

/** Registry / picker row. Counts are computed live rather than stored, so a
 *  stale counter can never disagree with the roster it labels. */
export interface BuyerTeamSummaryDto {
  id: string;
  name: string;
  description?: string | null;
  managerUserId?: string | null;
  managerDisplayName?: string | null;
  status: BuyerTeamStatus;
  createdAt: string;
  memberCount: number;
  specialistCount: number;
  /** Whether the CALLER may edit this team — resolved server-side (§3.3): an
   *  all-teams holder for every team, otherwise only a team the caller manages
   *  (its `managerUserId`, or a Manager-role membership). Render edit
   *  affordances from this; the service enforces the same rule on every write.
   *  Optional on the type so existing fixtures keep compiling. */
  canManage?: boolean;
}

/** One roster row. */
export interface BuyerTeamMemberDto {
  membershipId: string;
  userId: string;
  displayName?: string | null;
  email?: string | null;
  roleInTeam: BuyerTeamRole;
  joinedAtUtc: string;
  /**
   * 🔴 Buyers this PERSON is currently carrying — their whole book, NOT this
   * team's share of it. Capacity follows the person (D2), so a specialist
   * holding a lead that belongs to another team counts it here. It is
   * therefore not expected to reconcile with the team's `leadCount`, and any
   * surface rendering it owes the reader that caption.
   */
  activeBuyers: number;
}

/**
 * Team detail — the registry row plus its roster and lead rollups.
 *
 * 🔴 `activeBuyersHeldByMembers` and `leadCount` deliberately DO NOT
 * reconcile. Rosters and capacity follow the PERSON; lead counts, conversion
 * and ad ROI follow the LEAD (D2). Pinned server-side by
 * `CrossTeamHoldingTests` — do not "fix" it on either tier.
 */
export interface BuyerTeamDetailDto {
  id: string;
  name: string;
  description?: string | null;
  managerUserId?: string | null;
  managerDisplayName?: string | null;
  status: BuyerTeamStatus;
  createdAt: string;
  memberCount: number;
  specialistCount: number;
  /** Leads whose `buyerTeamId` is this team, archived excluded. */
  leadCount: number;
  /** …of those, still waiting for an owner. */
  unassignedLeadCount: number;
  /** Sum over the roster of what each member is carrying. */
  activeBuyersHeldByMembers: number;
  members: BuyerTeamMemberDto[];
  /** Same rule as `BuyerTeamSummaryDto.canManage`. */
  canManage?: boolean;
}

export interface CreateBuyerTeamRequest {
  name: string;
  description?: string | null;
  managerUserId?: string | null;
}

/** FIND-12 semantics: null/omitted = unchanged; an empty string CLEARS
 *  description / managerUserId. Name cannot be cleared, only renamed. */
export interface UpdateBuyerTeamRequest {
  name?: string | null;
  description?: string | null;
  managerUserId?: string | null;
}

export interface AddBuyerTeamMemberRequest {
  userId: string;
  roleInTeam: BuyerTeamRole;
}

export const buyerTeamsApi = {
  list: (includeArchived = false) =>
    api.get<BuyerTeamSummaryDto[]>("/buyer-teams", { includeArchived }),
  get: (id: string) => api.get<BuyerTeamDetailDto>(`/buyer-teams/${id}`),
  create: (body: CreateBuyerTeamRequest) =>
    api.post<BuyerTeamSummaryDto>("/buyer-teams", body),
  update: (id: string, body: UpdateBuyerTeamRequest) =>
    api.patch<BuyerTeamSummaryDto>(`/buyer-teams/${id}`, body),
  archive: (id: string) => api.post<BuyerTeamSummaryDto>(`/buyer-teams/${id}/archive`),
  unarchive: (id: string) => api.post<BuyerTeamSummaryDto>(`/buyer-teams/${id}/unarchive`),
  /** Add — or MOVE, when the user is already on another team (D1). Returns the
   *  team this call put them ON; the team they came off is not in the payload,
   *  which is why every caller invalidates the whole `buyerTeamKeys.all`
   *  prefix rather than one detail key. */
  addMember: (id: string, body: AddBuyerTeamMemberRequest) =>
    api.post<BuyerTeamDetailDto>(`/buyer-teams/${id}/members`, body),
  removeMember: (id: string, userId: string) =>
    api.delete<BuyerTeamDetailDto>(`/buyer-teams/${id}/members/${encodeURIComponent(userId)}`),
};
